import type { GroupPublic } from '@repo/types/groups';
import type { UserPublic } from '@repo/types/users';

import { Plus, User, Users } from 'lucide-react';

import MeetingFormContentLabelIcon from '@/components/features/calendar/form/meeting-form-content/MeetingFormContentLabelIcon';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { EmptyStateContent, EmptyStateDescription, EmptyStateFooter, EmptyStateTitle } from '@/components/ui/empty-state/EmptyState';
import { Tag } from '@/components/ui/tags';
import { getAvatar } from '@/utils/getAvatar';

interface ParticipantsSectionProps {
  users: UserPublic[];
  groups: GroupPublic[];
  onRemoveUser: (id: number) => void;
  onRemoveGroup: (id: number) => void;
  onAddUsers: () => void;
  onAddGroups: () => void;
}

export function ParticipantsSection(participantsSectionProps: ParticipantsSectionProps) {
  const { users, groups, onRemoveUser, onRemoveGroup, onAddUsers, onAddGroups } = participantsSectionProps;
  return (
    <>
      {/* Groups list */}
      <div className={'flex gap-3 px-3 md:px-4'}>
        <MeetingFormContentLabelIcon>
          <Users />
        </MeetingFormContentLabelIcon>
        <div className={'w-full space-y-4'}>
          <div className={'flex flex-col gap-0.5'}>
            <h5 className={'font-medium'}>Grupy</h5>
            <p className={'text-text-tertiary text-xs'}>Użytkownicy z wybranych grup zostaną dodani automatycznie.</p>
          </div>
          <div className="flex flex-wrap gap-1">
            {groups.map(({ id, name, avatarURL }) => (
              <Tag key={id} onRemove={() => onRemoveGroup(id)} variant={'muted'} border>
                <Avatar size={'xs'}>
                  <AvatarImage src={avatarURL ? getAvatar(avatarURL) : undefined} alt={name} />
                  <AvatarFallback>
                    <Users />
                  </AvatarFallback>
                </Avatar>
                {name}
              </Tag>
            ))}
            {groups.length > 0 && (
              <Badge size="sm" variant="muted" border className="cursor-pointer" onClick={onAddGroups} asChild>
                <button type="button">
                  <Plus />
                  Dodaj
                </button>
              </Badge>
            )}

            {groups.length === 0 && (
              <EmptyState className="border-border-secondary bg-surface-primary-hover/25 w-full rounded-md border pt-4!">
                <EmptyStateContent>
                  <EmptyStateTitle className="text-sm!">Brak wybranych grup</EmptyStateTitle>
                  <EmptyStateDescription className="max-w-xs text-xs!">Wybierz grupy które będą uczestniczyć w spotkaniu</EmptyStateDescription>
                  <EmptyStateFooter>
                    <Button type={'button'} variant={'outline'} size="sm" onClick={onAddGroups}>
                      <Plus />
                      Dodaj grupy
                    </Button>
                  </EmptyStateFooter>
                </EmptyStateContent>
              </EmptyState>
            )}
          </div>
        </div>
      </div>

      {/* Users list */}
      <div className={'flex gap-3 px-3 md:px-4'}>
        <MeetingFormContentLabelIcon>
          <User />
        </MeetingFormContentLabelIcon>
        <div className={'w-full space-y-4'}>
          <div className={'flex flex-col gap-0.5'}>
            <h5 className={'font-medium'}>Użytkownicy</h5>
            <p className={'text-text-tertiary text-xs'}>Możesz dodać dodatkowych użytkowników, także tych już należących do wybranych grup.</p>
          </div>
          <div className="flex flex-wrap gap-1">
            {users.map(({ id, name, surname, avatarURL }) => (
              <Tag key={id} onRemove={() => onRemoveUser(id)} variant={'muted'} border>
                <Avatar size={'xs'}>
                  <AvatarImage src={avatarURL ? getAvatar(avatarURL) : undefined} alt={name + '_' + surname} />
                  <AvatarFallback>
                    <User />
                  </AvatarFallback>
                </Avatar>
                {name} {surname.at(0)}.
              </Tag>
            ))}
            {users.length > 0 && (
              <Badge variant="muted" border className="cursor-pointer" onClick={onAddUsers} asChild>
                <button type="button">
                  <Plus />
                  Dodaj
                </button>
              </Badge>
            )}

            {users.length === 0 && (
              <EmptyState className="border-border-secondary bg-surface-primary-hover/25 w-full rounded-md border pt-4!">
                <EmptyStateContent>
                  <EmptyStateTitle className="text-sm!">Brak wybranych użytkowników</EmptyStateTitle>
                  <EmptyStateDescription className="max-w-xs text-xs!">Wybierz użytkowników którzy będą uczestniczyć w spotkaniu</EmptyStateDescription>
                  <EmptyStateFooter>
                    <Button type={'button'} variant={'outline'} size="sm" onClick={onAddUsers}>
                      <Plus />
                      Dodaj użytkowników
                    </Button>
                  </EmptyStateFooter>
                </EmptyStateContent>
              </EmptyState>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
